import {
    ChangeDetectionStrategy,
    Component,
    computed,
    ElementRef,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';

import { FeatureOnboarding, FeatureOnboardingDirective } from 'app/feature-onboarding/feature-onboarding.directive';
import { FeatureOnboardingStore } from 'app/feature-onboarding/feature-onboarding.store';
import { DS_ICONS } from 'app/shared/components/icons/ds-icons.constants';
import { NavTab, NavTabComponent } from 'app/shared/components/nav-tab/nav-tab.component';
import { PopoverComponent } from 'app/shared/components/popover/popover.component';
import { AnalyticsService } from 'app/shared/services/analytics.service';

import { useNotificationCenter } from '../../composables/use-notification-center';
import { NotificationView } from '../../models/notification-view';
import { NotificationCenterPage, NotificationTab } from '../../pages/notification-center/notification-center.page';
import { NotificationActionService } from '../../services/notification-action.service';

export const NOTIFICATION_CENTER_ONBOARDING: FeatureOnboarding = {
    id: 'notification-center',
    image: 'assets/img/onboarding/notification-center@2x.png',
    titleKey: 'FEATURE_ONBOARDING_NOTIFICATION_CENTER_TITLE',
    descriptionKey: 'FEATURE_ONBOARDING_NOTIFICATION_CENTER_DESCRIPTION',
    placement: 'bottom-right',
};

@Component({
    selector: 'app-notification-popover',
    templateUrl: './notification-popover.container.html',
    styleUrl: './notification-popover.container.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    host: {
        '(document:keydown.escape)': 'closeOnEscape($event)',
    },
    imports: [FeatureOnboardingDirective, NavTabComponent, NotificationCenterPage, PopoverComponent],
})
export class NotificationPopoverContainer {
    private readonly translate = inject(TranslateService);
    private readonly analytics = inject(AnalyticsService);
    private readonly router = inject(Router);
    /** One dot at a time on the bell: the unread one waits until the onboarding is seen. */
    private readonly onboardingSeen = inject(FeatureOnboardingStore).isSeen(NOTIFICATION_CENTER_ONBOARDING.id);
    private readonly langChange = toSignal(this.translate.onLangChange);
    private readonly popover = viewChild(PopoverComponent);
    private readonly trigger = viewChild<unknown, ElementRef<HTMLElement>>('trigger', { read: ElementRef });

    protected readonly notifications = useNotificationCenter();
    protected readonly notificationAction = inject(NotificationActionService);
    protected readonly activeTab = signal<NotificationTab>('unread');
    protected readonly NOTIFICATION_CENTER_ONBOARDING = NOTIFICATION_CENTER_ONBOARDING;

    readonly active = input(false);

    protected readonly triggerTabs = computed<NavTab[]>(() => {
        this.langChange();

        return [
            {
                icon: DS_ICONS.notification,
                dot: this.notifications.hasUnread() && this.onboardingSeen(),
                active: this.active(),
                ariaLabel: this.translate.instant('NOTIFICATION_CENTER_TITLE'),
                ariaControls: 'notification-popover-content',
                ariaExpanded: this.popover()?.isOpen() ?? false,
                ariaHasPopup: 'dialog',
                onClick: () => this.toggle(),
            },
        ];
    });

    private toggle(): void {
        const popover = this.popover();
        if (!popover) return;

        if (this.active()) {
            popover.close();
            return;
        }

        if (!popover.isOpen()) {
            this.notifications.reload();
            this.analytics.viewNotificationsMenu();
        }
        popover.toggle();
    }

    protected discover(): void {
        void this.router.navigateByUrl('/notifications');
    }

    protected open(notification: NotificationView, popover: PopoverComponent): void {
        popover.close();
        this.notificationAction.handle(notification, this.activeTab());
    }

    protected closeOnEscape(event: Event): void {
        const popover = this.popover();
        if (!popover?.isOpen()) return;

        event.preventDefault();
        popover.close();
        this.trigger()?.nativeElement.querySelector('button')?.focus();
    }
}
